import { memo, useCallback, useState } from 'react';

import { usePrivateWorkspaceAgentGrayscaleSelection } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/usePrivateWorkspaceAgentGrayscaleSelection';
import { WorkspaceAgentGuidedVariantGrayscaleSettings } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantGrayscaleSettings';
import { WorkspaceAgentGuidedVariantSettingsActions } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSettingsActions';
import { type WorkspaceAgentGuidedVariantGenerateRequest } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/workspaceAgentGuidedVariantGenerateRequest';
import {
  buildWorkspaceAgentGrayscaleDescription,
  buildWorkspaceAgentGrayscalePrompt,
  WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID,
  type WorkspaceAgentGrayscaleFilterId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGrayscale';

export type PrivateWorkspaceAgentGuidedVariantGrayscaleSettingsProps = {
  onGenerate: (request: WorkspaceAgentGuidedVariantGenerateRequest) => void;
};

export const PrivateWorkspaceAgentGuidedVariantGrayscaleSettings = memo(
  ({ onGenerate }: PrivateWorkspaceAgentGuidedVariantGrayscaleSettingsProps) => {
    const selection = usePrivateWorkspaceAgentGrayscaleSelection();
    const [filterId, setFilterId] = useState<WorkspaceAgentGrayscaleFilterId>('neutral');
    const selectedFilter = WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID[filterId];
    const generateLabel = selection.imageCount > 0 ? `Generate ${selection.imageCount.toLocaleString()}` : 'Generate';

    const handleFilterChange = useCallback((nextFilterId: WorkspaceAgentGrayscaleFilterId) => {
      setFilterId(nextFilterId);
    }, []);

    const handleGenerate = useCallback(() => {
      if (selection.imageCount < 1) {
        return;
      }

      onGenerate({
        allowsJpeg: true,
        kind: 'content',
        build: (destination, output) =>
          buildWorkspaceAgentGrayscalePrompt({
            destination,
            filter: selectedFilter,
            imageCount: selection.imageCount,
            output,
            skippedFileCount: selection.skippedFileCount,
            skippedVideoCount: selection.skippedVideoCount,
          }),
        structuredDisplay: {
          description: buildWorkspaceAgentGrayscaleDescription({
            filter: selectedFilter,
            skippedFileCount: selection.skippedFileCount,
            skippedVideoCount: selection.skippedVideoCount,
          }),
          icon: 'contrast',
          title: 'Grayscale',
        },
      });
    }, [onGenerate, selectedFilter, selection.imageCount, selection.skippedFileCount, selection.skippedVideoCount]);

    return (
      <div className="flex flex-col gap-3">
        <WorkspaceAgentGuidedVariantGrayscaleSettings
          onFilterChange={handleFilterChange}
          previewImageUrl={selection.previewImageUrl}
          value={filterId}
        />
        <WorkspaceAgentGuidedVariantSettingsActions
          generateLabel={generateLabel}
          isGenerateDisabled={selection.imageCount < 1}
          onGenerate={handleGenerate}
        />
      </div>
    );
  },
);

PrivateWorkspaceAgentGuidedVariantGrayscaleSettings.displayName = 'PrivateWorkspaceAgentGuidedVariantGrayscaleSettings';
